import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Avatar,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ErrorIcon from '@mui/icons-material/Error';
import LogoutIcon from '@mui/icons-material/Logout';
import { useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import { useDispatch, useSelector } from 'react-redux';
import { clearUser } from '../store/userSlice';
import { UserRole } from '../types/user';
import kfintechLogo from '../assets/kfintech-logo.png';
import webileappsLogo from '../assets/webileapps.png';
import profileImage from '../assets/profile.png';

interface SidebarProps {
  onProfileClick?: () => void;
}

export default function Sidebar({ onProfileClick }: SidebarProps) {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRole = useSelector((state: any) => state.user.role) as UserRole | undefined;
  const userName = useSelector((state: any) => state.user.name) as string | undefined;
  const isSupport = userRole === UserRole.SUPPORT;
  const isAdmin = userRole === UserRole.ADMIN;
  const isAMCUser = userRole === UserRole.AMC;

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    dispatch(clearUser());
    navigate('/login');
  };

  const currentPath =
    location.pathname.startsWith('/payments')
      ? '/payments'
      : location.pathname.startsWith('/dashboard')
        ? '/dashboard'
        : location.pathname.startsWith('/reports')
          ? '/reports'
          : location.pathname.startsWith('/swift-flow-errors')
            ? '/swift-flow-errors'
            : location.pathname.startsWith('/bank-details')
              ? '/bank-details'
              : location.pathname.startsWith('/users')
                ? '/users'
                : location.pathname.startsWith('/docs')
                  ? '/docs'
                  : '/amcs';

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? 240 : 56,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 56,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
            <img src={webileappsLogo} alt="Webileapps" style={{ height: '100%', width: 'auto' }} />
          </Box>
          {open && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Box sx={{ height: 18, display: 'flex', alignItems: 'center' }}>
                <img src={kfintechLogo} alt="KFintech" style={{ height: '100%', width: 'auto' }} />
              </Box>
            </>
          )}
        </Box>
        <IconButton onClick={() => setOpen(!open)} size="small">
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentPath === '/dashboard'}
            onClick={() => handleNavigation('/dashboard')}
            sx={{
              px: 2.5,
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        {!isAMCUser && (
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/reports'}
              onClick={() => handleNavigation('/reports')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <AssessmentIcon />
              </ListItemIcon>
              <ListItemText primary="Reports" sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton
            selected={currentPath === '/amcs'}
            onClick={() => handleNavigation('/amcs')}
            sx={{
              px: 2.5,
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Configuration" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentPath === '/payments'}
            onClick={() => handleNavigation('/payments')}
            sx={{
              px: 2.5,
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <PaymentIcon />
            </ListItemIcon>
            <ListItemText primary="Payments" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        {isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/bank-details'}
              onClick={() => handleNavigation('/bank-details')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <AccountBalanceIcon />
              </ListItemIcon>
              <ListItemText primary="Bank Details" sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        )}
        {isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/swift-flow-errors'}
              onClick={() => handleNavigation('/swift-flow-errors')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <ErrorIcon />
              </ListItemIcon>
              <ListItemText primary="Errors Master" sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        )}
        {!isSupport && !isAMCUser && (
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/users'}
              onClick={() => handleNavigation('/users')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary="Users" sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        )}
        {isAdmin && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              window.open('/docs', '_blank', 'noopener,noreferrer');
            }}
            sx={{
              px: 2.5,
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <DescriptionIcon />
            </ListItemIcon>
            <ListItemText primary="Docs" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        )}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      {/* Profile section */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
          onClick={onProfileClick}
        >
          <Avatar src={profileImage} sx={{ width: 32, height: 32 }} />
          {open && (
            <Typography variant="body2" noWrap>
              {userName || 'Admin'}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              px: 2.5,
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}
